import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import skinFadeHero from "@/assets/high-skin-fade-bakakamad-topp.png.asset.json";
import { Faq } from "@/components/Faq";
import { ServiceHero } from "@/components/ServiceHero";
import { Button } from "@/components/ui/button";
import { breadcrumbSchema, faqSchema, jsonLd, SITE_URL, absUrl } from "@/lib/seo";
import { GENERIC_BOKADIREKT } from "@/lib/booking";
import { internalPath } from "@/lib/paths";
import { FadeGallery } from "@/components/FadeGallery";
import { ContextLinkBlock } from "@/components/ContextLinkBlock";
import { SERVICE_SALON_LINKS } from "@/data/localServiceLinks";
import midSkinBak from "@/assets/mid-skin-fade-bakifran.webp.asset.json";
import midSkinSida from "@/assets/mid-skin-fade-sida.webp.asset.json";
import midSkinNarbild from "@/assets/mid-skin-fade-narbild-overgang.webp.asset.json";
import vitToppSida from "@/assets/skin-fade-vit-topp-sida.webp.asset.json";
import vitToppBak from "@/assets/skin-fade-vit-topp-bakifran.webp.asset.json";
import vitToppNarbild from "@/assets/skin-fade-vit-topp-narbild.webp.asset.json";

const BOOK_URL = GENERIC_BOKADIREKT;

const taperDefinition =
  "En taper fade är en gradvis övergång där håret blir kortare nedåt mot nacken och tinningarna, utan att huden blottas som vid en skin fade. De tre vanligaste varianterna är low, mid och high taper. Barberaren bygger övergången med maskin i flera längder för ett mjukt och jämnt resultat.";

const skinDefinition =
  "En skin fade är en gradvis övergång där huden syns längst ned vid nacken och tinningarna, till skillnad från en taper fade som lämnar kort hår kvar. Barberaren bygger övergången uppåt i flera längder och använder rakkniv eller maskin utan skydd längst ned för ett rent och tydligt avslut.";

const fadeDifference =
  "En taper fade lämnar kort hår kvar längst ned vid nacken och tinningarna. En skin fade går hela vägen ned till synlig hud. Taper fade ger därför ett mjukare resultat, medan skin fade skapar tydligare kontrast.";

const fadePrice =
  "En fade kostar 450 kr hos Tailor's Barber. Priset gäller taper fade och skin fade på alla våra fyra salonger i Stockholm.";

const variants = [
  {
    name: "Low Skin Fade",
    desc: "Huden syns först strax ovanför öronen. Diskret nog för de flesta arbetsplatser.",
  },
  {
    name: "Mid Skin Fade",
    desc: "Övergången startar mitt på sidan. Den vanligaste beställningen hos oss.",
  },
  {
    name: "High Skin Fade",
    desc: "Huden går högt upp mot tinningen. Maximal kontrast mot toppen.",
  },
  {
    name: "Bald Fade / Zero Fade",
    desc: "Nollat vid basen med nollmaskin. Renast möjliga avslut.",
  },
  { name: "Drop Fade", desc: "Linjen böjer av nedåt bakom örat och följer huvudets form." },
  { name: "Burst Fade", desc: "Huden fasas i en båge runt örat, resten av nacken lämnas längre." },
];

const suits = [
  {
    title: "Tjockt, rakt hår",
    desc: "Skin fade tar bort volymen på sidorna och gör siluetten smalare och renare.",
  },
  {
    title: "Lockigt och krulligt hår",
    desc: "Kontrasten mellan hud och lock blir tydlig, en av de snyggaste kombinationerna.",
  },
  {
    title: "Tunnare hårfäste",
    desc: "Kort på sidorna drar mindre uppmärksamhet till toppen än långa sidor gör.",
  },
  {
    title: "Skägg",
    desc: "Faden kan kopplas ihop med skägget så att övergången löper obruten från tinning till käke.",
  },
];

const askFor = [
  "Säg var huden ska börja: vid öronen (low), mitt på sidan (mid) eller vid tinningen (high).",
  "Säg om nacken ska vara rundad, fyrkantig eller följa den naturliga hårlinjen.",
  "Ange längden på toppen i centimeter eller visa med fingrarna. Maskinnummer räcker sällan.",
  "Berätta om faden ska kopplas ihop med skägget eller sluta vid kindlinjen.",
  "Har du en bild, visa den. Vi säger till om den fungerar med din hårtyp innan vi börjar.",
];

const faq = [
  { q: "Vad är en taper fade?", a: taperDefinition },
  { q: "Vad är en skin fade?", a: skinDefinition },
  { q: "Vad är skillnaden mellan taper fade och skin fade?", a: fadeDifference },
  { q: "Vad kostar en fade i Stockholm?", a: fadePrice },
  {
    q: "Är bald fade och zero fade samma sak som skin fade?",
    a: "Ja, i praktiken. Bald fade, zero fade och nollfade beskriver alla att håret nollas mot huden. Vi använder skin fade som samlingsnamn och stämmer alltid av nivån i konsultationen.",
  },
  {
    q: "Hur lång tid tar en skin fade?",
    a: "Räkna med 35 till 50 minuter. Nollningen och blendningen tar längre tid än en vanlig klippning eftersom varje övergång måste kontrolleras i flera vinklar.",
  },
  {
    q: "Hur ofta behöver jag klippa om en skin fade?",
    a: "Varannan till var tredje vecka. Utväxningen syns snabbt just för att basen är helt ren. Redan efter tio dagar börjar kontrasten mjukna.",
  },
  {
    q: "Passar skin fade alla hårtyper?",
    a: "Nästan alla. Tjockt, lockigt och krulligt hår ger starkast resultat. Har du ärr, födelsemärken eller känslig hud på skalpen tittar vi på det först och föreslår en låg taper i stället om det behövs.",
  },
  {
    q: "Kan jag komma på drop-in för skin fade?",
    a: "Ja, i mån av tid på alla fyra salonger. Eftersom en skin fade tar längre tid än en standardklippning rekommenderar vi bokning på helger.",
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Skin Fade Stockholm",
  alternateName: ["Bald Fade", "Zero Fade", "Nollfade", "High Skin Fade", "Low Skin Fade"],
  description:
    "Skin fade i Stockholm hos Tailor's Barber. Low, mid och high skin fade samt bald fade och zero fade, klippt av barberare på fyra salonger.",
  image: [absUrl(midSkinSida.url)],

  provider: { "@type": "BarberShop", name: "Tailor's Barber", url: SITE_URL },
  areaServed: { "@type": "City", name: "Stockholm" },
  offers: { "@type": "Offer", price: "450", priceCurrency: "SEK" },
};

export const Route = createFileRoute("/skin-fade")({
  head: () => ({
    meta: [
      { title: "Skin fade: vad det är och vad det kostar | Tailor's Barber" },
      {
        name: "description",
        content:
          "En skin fade visar huden längst ned och byggs mjukt uppåt med flera längder. Low, mid och high. 450 kr på våra fyra salonger i Stockholm.",
      },
      {
        property: "og:title",
        content: "Skin fade: vad det är och vad det kostar | Tailor's Barber",
      },
      {
        property: "og:description",
        content:
          "En skin fade visar huden längst ned och byggs mjukt uppåt med flera längder. Low, mid och high. 450 kr på våra fyra salonger i Stockholm.",
      },
      { property: "og:url", content: absUrl("/skin-fade") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: absUrl("/skin-fade") }],
    scripts: [
      jsonLd(serviceSchema),
      jsonLd(faqSchema(faq)),
      jsonLd(
        breadcrumbSchema([
          { name: "Hem", url: "/" },
          { name: "Skin Fade", url: "/skin-fade" },
        ]),
      ),
    ],
  }),
  component: SkinFadePage,
});

function SkinFadePage() {
  return (
    <SiteShell>
      <ServiceHero
        breadcrumbName="Skin Fade"
        eyebrow="Skin Fade Stockholm"
        title={<>Skin Fade i Stockholm hos Tailor&apos;s Barber</>}
        directAnswerHeading="Vad är en skin fade?"
        directAnswer={skinDefinition}
        intro={
          <>
            En skin fade är den mest kontrastrika klippningen vi gör: håret nollas mot huden vid
            basen och byggs sedan upp mjukt uppåt. Den kräver mer handarbete än en vanlig klippning
            och blir bara bra om övergången kontrolleras i flera vinklar. Vi klipper low, mid och
            high skin fade samt bald fade och zero fade på alla fyra salonger i Stockholm.
          </>
        }
        priceLabel="Från 450 kr"
        durationLabel="45 min"
        ctaLabel="Boka skin fade"
        ctaHref={BOOK_URL}
        image={skinFadeHero.url}
        imageAlt="High skin fade från sidan med bakåtkammad brun topp hos Tailor's Barber i Stockholm"
        eager
      />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Så bygger vi en skin fade</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Det som avgör kvaliteten är inte nollningen i sig utan zonen strax ovanför. Där ska ingen
          synlig kant finnas kvar. Vi arbetar först med maskin i flera guidelängder, jämnar sedan ut
          med clipper-over-comb och avslutar med nollmaskin eller rakkniv längs hårfästet.
        </p>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Eftersom huden syns direkt syns också varje missad linje. Därför tar en skin fade längre
          tid än en standardklippning, och därför kontrollerar vi resultatet i dagsljus och från
          flera håll innan styling. Det är också en av sakerna som skiljer en{" "}
          <Link to={internalPath("/barbershop-stockholm/")} className="underline font-medium">
            barbershop från en vanlig frisörsalong
          </Link>
          . Vill du se hur faden ser ut i andra klippningar hittar du fler{" "}
          <Link to={internalPath("/herrfrisyrer-2026/")} className="underline font-medium">
            herrfrisyrer 2026
          </Link>{" "}
          i vår guide.
        </p>
      </section>

      <FadeGallery
        heading="Skin fade klippta hos oss"
        intro="Mid skin fade från tre vinklar, bakifrån, från sidan och i närbild där huden möter håret."
        items={[
          {
            src: midSkinBak.url,
            alt: "Mid skin fade bakifrån hos Tailor's Barber i Stockholm",
            caption: "Mid skin fade bakifrån, med nollad bas och längden byggd mjukt uppåt.",
          },
          {
            src: midSkinSida.url,
            alt: "Mid skin fade från sidan med bakåtkammad topp",
            caption:
              "Mid skin fade från sidan, där huden börjar mitt på sidan och möter en bakåtkammad topp.",
          },
          {
            src: midSkinNarbild.url,
            alt: "Närbild på övergången mellan hud och hår i en mid skin fade",
            caption: "Närbild på övergången i en mid skin fade, där huden möter det korta håret.",
          },
          {
            src: vitToppSida.url,
            alt: "High skin fade på vitt hår från sidan hos Tailor's Barber",
            caption: "High skin fade på vitt hår, med hög start och bakåtkammad topp.",
          },
          {
            src: vitToppBak.url,
            alt: "High skin fade på vitt hår bakifrån med ren nacklinje",
            caption: "High skin fade bakifrån, med nollad nacke och övergång utan synlig kant.",
          },
          {
            src: vitToppNarbild.url,
            alt: "Närbild på high skin fade vid tinning och öra på vitt hår",
            caption:
              "Närbild på high skin fade vid tinningen, med en ren övergång hela vägen fram.",
          },
        ]}
      />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Skillnaden mellan taper fade och skin fade</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          En taper fade lämnar kort hår kvar längst ned vid nacken och tinningarna. En skin fade går
          hela vägen ned till synlig hud. Taper fade ger därför ett mjukare resultat, medan skin
          fade skapar tydligare kontrast. Läs mer om det mjukare alternativet på sidan om{" "}
          <Link to="/taper-fade/" className="underline font-medium">
            taper fade
          </Link>
          .
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Varianter av skin fade</h2>
        <ul className="mt-6 grid gap-3 md:grid-cols-2 max-w-4xl">
          {variants.map((v) => (
            <li key={v.name} className="card-elev p-5">
              <strong>{v.name}</strong>
              <p className="mt-1 text-sm text-foreground/80">{v.desc}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Vem passar en skin fade?</h2>
        <div className="mt-6 grid gap-5 md:grid-cols-2 max-w-4xl">
          {suits.map((s) => (
            <div key={s.title} className="card-elev p-6">
              <h3 className="text-lg font-medium">{s.title}</h3>
              <p className="mt-2 text-sm text-foreground/80">{s.desc}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Har du känslig hud eller ärr på skalpen är en låg taper ofta ett bättre val, eftersom en
          liten stubb döljer mer än ren hud gör. Det tittar vi på i konsultationen innan maskinen
          sätts i.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Så beställer du en skin fade</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          &quot;Skin fade&quot; ensamt räcker sällan. Ordet beskriver bara att basen går till hud,
          inte hur högt eller hur nacken ska se ut. Fyra korta besked ger oss allt vi behöver:
        </p>
        <ol className="mt-6 space-y-3 max-w-3xl list-decimal list-inside text-base md:text-lg text-foreground/85">
          {askFor.map((s, i) => (
            <li key={i} className="leading-relaxed">
              {s}
            </li>
          ))}
        </ol>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Underhåll, hur ofta och hur hemma</h2>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          En skin fade håller sig skarp i ungefär två veckor och ser bra ut i tre. Efter det växer
          basen ikapp och kontrasten försvinner. Bokar du var tredje vecka behåller du looken året
          runt.
        </p>
        <p className="mt-4 text-base md:text-lg text-foreground/80 max-w-3xl leading-relaxed">
          Hemma behöver sidorna inget särskilt utöver vanlig tvätt. Nyrakad hud kan bli torr. En
          enkel fuktkräm dagen efter räcker. Undvik att själv köra maskin längs nacklinjen, det är
          den vanligaste orsaken till att faden blir ojämn innan nästa besök.
        </p>
      </section>

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Pris för skin fade i Stockholm</h2>
        <ul className="mt-4 space-y-2 text-base md:text-lg text-foreground/85 max-w-3xl">
          <li>
            <strong>Skin fade:</strong> 450 kr
          </li>
          <li>
            <strong>Skin fade + skäggtrimning:</strong> 680 kr
          </li>
        </ul>
        <p className="mt-4 text-foreground/70 max-w-3xl">
          Tillgängligt på alla fyra salonger, alla dagar i veckan. Söker du en{" "}
          <Link
            to={internalPath("/barberare-stockholm-ahlens-city/")}
            className="underline font-medium"
          >
            Barber Stockholm City
          </Link>{" "}
          nära T-Centralen bokar du enklast på Åhléns City. Se hela vår{" "}
          <Link to="/priser/" className="underline">
            prislista
          </Link>
          .
        </p>
      </section>

      <ContextLinkBlock
        heading={SERVICE_SALON_LINKS["skin-fade"]!.heading}
        intro={SERVICE_SALON_LINKS["skin-fade"]!.intro}
        links={SERVICE_SALON_LINKS["skin-fade"]!.links}
      />

      <Faq items={faq} heading="Vanliga frågor om skin fade" />

      <section className="container-page py-10">
        <h2 className="text-2xl md:text-3xl">Relaterade sidor</h2>
        <ul className="mt-4 flex flex-wrap gap-3">
          <li>
            <Link to="/taper-fade/" className="btn-outline">
              Taper Fade
            </Link>
          </li>
          <li>
            <Link to="/herrklippning/" className="btn-outline">
              Herrklippning
            </Link>
          </li>
          <li>
            <Link to="/skaggtrimning/" className="btn-outline">
              Skäggtrimning
            </Link>
          </li>
          <li>
            <Link to="/barberare-stockholm-ahlens-city/" className="btn-outline">
              Åhléns City
            </Link>
          </li>
        </ul>
      </section>

      <section className="container-page pb-16">
        <div className="card-elev p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl">Redo för din skin fade?</h2>
          <p className="mt-3 text-foreground/80">Boka online eller kom förbi på drop-in.</p>
          <div className="mt-6">
            <Button asChild size="lg">
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer">
                Boka skin fade
              </a>
            </Button>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
